import type { JSX } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { OrganizationSwitcher } from '@clerk/clerk-react';
import { useAppContext } from '@/context/AppContext';
import { usePlatformAdminStatus } from '@/context/PlatformAdminContext';
import { KanjoyWordmark } from '@/components/primitives/KanjoyWordmark';
import { Badge } from '@/components/primitives/Badge';
import styles from './Sidebar.module.css';

const ORG_SWITCHER_APPEARANCE = {
  elements: {
    rootBox: { width: '100%' },
    organizationSwitcherTrigger: {
      width: '100%',
      padding: 'var(--space-2) var(--space-3)',
      background: 'transparent',
      border: 'none',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-sm)',
      fontWeight: 'var(--weight-semibold)',
      justifyContent: 'space-between',
      '&:hover': { background: 'var(--sidebar-hover-bg)' },
      '&:focus': { boxShadow: 'var(--focus-ring)' },
    },
    organizationSwitcherTriggerIcon: {
      color: 'var(--sidebar-text-dim)',
    },
    // Scope white text to the trigger only — dropdown uses default dark-on-white
    organizationPreviewMainIdentifier__organizationSwitcherTrigger: {
      color: 'var(--text-inverse)',
    },
    organizationPreviewSecondaryIdentifier__organizationSwitcherTrigger: {
      color: 'var(--sidebar-text-dim)',
    },
  },
} as const;

interface NavItem {
  label: string;
  to: string;
  icon: JSX.Element;
  section: 'Overview' | 'Manage' | 'Reports' | 'Platform';
  /**
   * Org-admin-only nav item. Set true to restrict the entry to
   * `currentUser.role === 'admin'` and render a small "Admin" badge
   * beside the label. Non-admins do not see the entry at all (mirrors
   * the platform-admin nav-hide pattern below). Task 159c.
   */
  adminOnly?: boolean;
}

const ICON_COMMON = {
  viewBox: '0 0 18 18',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const NAV_ITEMS: readonly NavItem[] = [
  {
    label: 'Dashboard',
    to: '/dashboard',
    section: 'Overview',
    icon: (
      <svg {...ICON_COMMON}>
        <rect x="1.5" y="1.5" width="6" height="6" rx="1" />
        <rect x="10.5" y="1.5" width="6" height="6" rx="1" />
        <rect x="1.5" y="10.5" width="6" height="6" rx="1" />
        <rect x="10.5" y="10.5" width="6" height="6" rx="1" />
      </svg>
    ),
  },
  {
    label: 'Team Stats',
    to: '/team',
    section: 'Overview',
    icon: (
      <svg {...ICON_COMMON}>
        <path d="M12.75 15.75v-1.5a3 3 0 0 0-3-3h-4.5a3 3 0 0 0-3 3v1.5" />
        <circle cx="8.25" cy="5.25" r="3" />
        <path d="M16.5 15.75v-1.5a3 3 0 0 0-2.25-2.9" />
        <path d="M12 2.4a3 3 0 0 1 0 5.7" />
      </svg>
    ),
  },
  {
    label: 'Domains',
    to: '/analytics/domains',
    section: 'Overview',
    icon: (
      <svg {...ICON_COMMON}>
        <circle cx="9" cy="9" r="7.5" />
        <path d="M1.5 9h15" />
        <path d="M9 1.5a11.25 11.25 0 0 1 3 7.5 11.25 11.25 0 0 1-3 7.5" />
        <path d="M9 1.5a11.25 11.25 0 0 0-3 7.5 11.25 11.25 0 0 0 3 7.5" />
      </svg>
    ),
  },
  {
    label: 'Clients & Projects',
    to: '/clients',
    section: 'Manage',
    icon: (
      <svg {...ICON_COMMON}>
        <path d="M15.75 15v-1.5a3 3 0 0 0-3-3H5.25a3 3 0 0 0-3 3V15" />
        <circle cx="9" cy="5.25" r="3" />
      </svg>
    ),
  },
  {
    label: 'Categorize',
    to: '/categorize',
    section: 'Manage',
    icon: (
      <svg {...ICON_COMMON}>
        <path d="M2.25 3h13.5" />
        <path d="M4.5 9h9" />
        <path d="M6.75 15h4.5" />
      </svg>
    ),
  },
  {
    label: 'Rules',
    to: '/rules',
    section: 'Manage',
    icon: (
      <svg {...ICON_COMMON}>
        <path d="M3 6l3 3-3 3" />
        <path d="M9 15h6" />
      </svg>
    ),
  },
  {
    label: 'Monthly Report',
    to: '/reports/monthly',
    section: 'Reports',
    icon: (
      <svg {...ICON_COMMON}>
        <path d="M13.5 2.25H4.5a1.5 1.5 0 0 0-1.5 1.5v10.5a1.5 1.5 0 0 0 1.5 1.5h9a1.5 1.5 0 0 0 1.5-1.5V3.75a1.5 1.5 0 0 0-1.5-1.5z" />
        <path d="M6 6h6" />
        <path d="M6 9h6" />
        <path d="M6 12h3" />
      </svg>
    ),
  },
  {
    label: 'Applications',
    to: '/reports/applications',
    section: 'Reports',
    icon: (
      // Task 117: generic grid-of-squares, mirroring the Dashboard icon
      // style. Applications report shows how the team's time splits
      // across apps; the grid reads as "many small tiles".
      <svg {...ICON_COMMON}>
        <rect x="2" y="2" width="4" height="4" rx="0.75" />
        <rect x="7" y="2" width="4" height="4" rx="0.75" />
        <rect x="12" y="2" width="4" height="4" rx="0.75" />
        <rect x="2" y="7" width="4" height="4" rx="0.75" />
        <rect x="7" y="7" width="4" height="4" rx="0.75" />
        <rect x="12" y="7" width="4" height="4" rx="0.75" />
        <rect x="2" y="12" width="4" height="4" rx="0.75" />
        <rect x="7" y="12" width="4" height="4" rx="0.75" />
        <rect x="12" y="12" width="4" height="4" rx="0.75" />
      </svg>
    ),
  },
  {
    label: 'Grouped Report',
    to: '/reports/grouped',
    section: 'Reports',
    icon: (
      // Task 160: nested-rows glyph hinting at the two-level breakdown.
      <svg {...ICON_COMMON}>
        <rect x="2" y="3" width="14" height="3" rx="0.75" />
        <rect x="5" y="8" width="11" height="2.5" rx="0.5" />
        <rect x="5" y="12" width="11" height="2.5" rx="0.5" />
      </svg>
    ),
  },
  {
    label: 'Activity Log',
    to: '/reports/activity-log',
    section: 'Reports',
    icon: (
      // Task 169: stacked-lines glyph hinting at a flat log of (app, title)
      // entries. Distinct from the Grouped Report glyph (which has a
      // taller header bar) and the Applications grid.
      <svg {...ICON_COMMON}>
        <path d="M3 4.5h12" />
        <path d="M3 9h12" />
        <path d="M3 13.5h8" />
      </svg>
    ),
  },
  {
    label: 'Work Log Review',
    to: '/reports/work-log-review',
    section: 'Reports',
    adminOnly: true,
    icon: (
      // Task 159c: a magnifier glyph hinting at "review" — the page is
      // a review queue, not another rollup report.
      <svg {...ICON_COMMON}>
        <circle cx="8" cy="8" r="5" />
        <path d="M12 12 L15.5 15.5" />
      </svg>
    ),
  },
];

// Task 130 D6 — platform-admin nav entries, appended only when the
// `PlatformAdminContext` reports `admin`. Kept in a separate constant so
// the hide-not-show pattern is structurally enforced — a non-admin never
// sees this section label, let alone the link.
const PLATFORM_NAV_ITEMS: readonly NavItem[] = [
  {
    label: 'Tenants',
    to: '/platform/tenants',
    section: 'Platform',
    icon: (
      <svg {...ICON_COMMON}>
        <path d="M2.25 4.5 9 1.5l6.75 3L9 7.5 2.25 4.5z" />
        <path d="M2.25 9 9 12l6.75-3" />
        <path d="M2.25 13.5 9 16.5l6.75-3" />
      </svg>
    ),
  },
  {
    label: 'Hosts',
    to: '/platform/hosts',
    section: 'Platform',
    icon: (
      <svg {...ICON_COMMON}>
        <circle cx="9" cy="9" r="6.5" />
        <path d="M2.5 9 H15.5" />
        <path d="M9 2.5 c2 2 2 11 0 13 c-2 -2 -2 -11 0 -13 z" />
      </svg>
    ),
  },
  {
    label: 'Taxonomy',
    to: '/platform/categories',
    section: 'Platform',
    icon: (
      <svg {...ICON_COMMON}>
        <path d="M9 2 v3" />
        <rect x="6" y="5" width="6" height="3" rx="0.5" />
        <path d="M9 8 v3" />
        <path d="M3 11 h12" />
        <rect x="2" y="11" width="3" height="3" rx="0.5" />
        <rect x="7.5" y="11" width="3" height="3" rx="0.5" />
        <rect x="13" y="11" width="3" height="3" rx="0.5" />
      </svg>
    ),
  },
];

function computeSectionVisibility(items: readonly NavItem[]): boolean[] {
  const visibility: boolean[] = [];
  let lastSection: string | undefined;
  for (const item of items) {
    visibility.push(item.section !== lastSection);
    lastSection = item.section;
  }
  return visibility;
}

export function Sidebar() {
  const { currentUser, currentOrg, sidebarCollapsed, setSidebarCollapsed, signOut, mockupMode } =
    useAppContext();
  const platformAdminStatus = usePlatformAdminStatus();
  // Task 159c: org-admin-only items (e.g. Work Log Review) are filtered
  // out for non-admins entirely — no anti-feature-discovery surface for
  // members. Mirrors the platform-admin hide-not-show pattern below.
  const isOrgAdmin = currentUser.role === 'admin';
  const visibleOrgItems = NAV_ITEMS.filter((item) => !item.adminOnly || isOrgAdmin);
  const navItems =
    platformAdminStatus === 'admin' ? [...visibleOrgItems, ...PLATFORM_NAV_ITEMS] : visibleOrgItems;
  const sectionVisibility = computeSectionVisibility(navItems);
  const userInitials = currentUser.name
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('');

  return (
    <nav
      className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}
      aria-label="Main navigation"
    >
      <div className={styles.logo}>
        {sidebarCollapsed ? <span className={styles.logoMark}>K</span> : <KanjoyWordmark />}
      </div>

      <div className={styles.navWrap}>
        {navItems.map((item, i) => (
          <div key={item.to}>
            {sectionVisibility[i] && !sidebarCollapsed && (
              <div className={styles.sectionLabel}>{item.section}</div>
            )}
            <NavLink
              to={item.to}
              className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
              aria-label={item.label}
            >
              <span className={styles.navIcon} aria-hidden="true">
                {item.icon}
              </span>
              {!sidebarCollapsed && (
                <span className={styles.navLabel}>
                  {item.label}
                  {item.adminOnly && (
                    <span className={styles.adminBadge}>
                      <Badge variant="admin">Admin</Badge>
                    </span>
                  )}
                </span>
              )}
            </NavLink>
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        {!sidebarCollapsed && (
          <>
            {mockupMode ? (
              <div className={styles.orgSwitcher}>
                <span className={styles.orgName}>{currentOrg.name}</span>
                <span className={styles.orgChevron} aria-hidden="true">
                  ▾
                </span>
              </div>
            ) : (
              <OrganizationSwitcher
                hidePersonal
                appearance={ORG_SWITCHER_APPEARANCE}
                createOrganizationMode="modal"
                afterCreateOrganizationUrl="/"
                afterSelectOrganizationUrl="/"
              />
            )}
            <div className={styles.divider} />
          </>
        )}
        <div className={styles.userArea}>
          <div className={styles.userAvatar} aria-hidden="true">
            {userInitials}
          </div>
          {!sidebarCollapsed && (
            <>
              <div className={styles.userInfo}>
                <div className={styles.userName}>{currentUser.name}</div>
                <div className={styles.userRole}>
                  {currentUser.role === 'admin' ? 'Admin' : 'Member'}
                </div>
              </div>
              <div className={styles.userActions}>
                <Link
                  to="/settings/organization"
                  className={styles.userActionBtn}
                  aria-label="Settings"
                >
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="8" cy="8" r="2.5" />
                    <path d="M13.5 8a5.5 5.5 0 0 0-.11-1.1l1.5-1.17-1-1.73-1.8.56a5.5 5.5 0 0 0-1.9-1.1L9.89 1.5H7.89l-.3 1.96a5.5 5.5 0 0 0-1.9 1.1l-1.8-.56-1 1.73 1.5 1.17A5.5 5.5 0 0 0 4.28 8c0 .37.04.74.11 1.1l-1.5 1.17 1 1.73 1.8-.56c.55.48 1.19.85 1.9 1.1l.3 1.96h2l.3-1.96a5.5 5.5 0 0 0 1.9-1.1l1.8.56 1-1.73-1.5-1.17c.07-.36.11-.73.11-1.1z" />
                  </svg>
                </Link>
                <button
                  type="button"
                  className={styles.userActionBtn}
                  aria-label="Sign out"
                  onClick={signOut}
                >
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 14H3.33A1.33 1.33 0 0 1 2 12.67V3.33A1.33 1.33 0 0 1 3.33 2H6" />
                    <polyline points="10 11.33 14 8 10 4.67" />
                    <line x1="14" y1="8" x2="6" y2="8" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          className={styles.collapseBtn}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? '»' : '«'}
        </button>
      </div>
    </nav>
  );
}
